import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, X, Upload, ImageIcon } from "lucide-react";
import { API, adminApi } from "./AdminDashboard";

const empty = { title: "", date: "", sort_date: "", description: "", photos: [] };
const inputCls = "w-full rounded-xl bg-white/70 border border-[#E2E8E4] px-4 py-2.5 text-sm text-[#1A2F24] placeholder:text-[#5C7368]/60 focus:outline-none focus:border-[#F4A261] transition-colors duration-300";

const PastEventsTab = () => {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => adminApi().get(`${API}/past-events`).then((r) => setEvents(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const uploadPhotos = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await adminApi().post(`${API}/admin/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, photos: [...f.photos, data.url] }));
      }
      toast.success("Photo(s) uploaded.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.photos.length === 0) { toast.error("Add at least one photo."); return; }
    setSaving(true);
    try {
      if (editingId) {
        await adminApi().put(`${API}/admin/past-events/${editingId}`, form);
        toast.success("Past event updated.");
      } else {
        await adminApi().post(`${API}/admin/past-events`, form);
        toast.success("Past event added.");
      }
      setForm(empty);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save event.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await adminApi().delete(`${API}/admin/past-events/${id}`);
      toast.success("Past event removed.");
      load();
    } catch {
      toast.error("Could not remove event.");
    }
  };

  return (
    <div className="grid grid-cols-12 gap-4 lg:gap-8" data-testid="admin-past-events-tab">
      <form onSubmit={save} className="col-span-12 lg:col-span-5 rounded-2xl border border-[#E2E8E4] bg-[#F2F4F0] p-7" data-testid="past-event-form">
        <h3 className="font-display text-2xl text-[#1A2F24]">{editingId ? "Edit Past Event" : "Add Past Event"}</h3>
        <div className="mt-5 space-y-3">
          <input required placeholder="Event name" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="past-event-title" className={inputCls} />
          <input required placeholder="Date label (e.g. October 2026)" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="past-event-date" className={inputCls} />
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-[#5C7368] mb-1.5 ml-1">Event date (for sorting, newest first)</label>
            <input required type="date" value={form.sort_date} onChange={(e) => setForm({ ...form, sort_date: e.target.value })} data-testid="past-event-sort-date" className={inputCls} />
          </div>
          <textarea required rows={3} placeholder="Short description (1–2 sentences)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="past-event-description" className={inputCls} />
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-[#5C7368] mb-1.5 ml-1">Photos (first one becomes the cover)</label>
            <label className={`flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#E2E8E4] bg-white/60 py-6 text-xs tracking-[0.12em] uppercase text-[#5C7368] cursor-pointer transition-colors duration-300 hover:border-[#F4A261] hover:text-[#F4A261] ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="past-event-upload">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {uploading ? "Uploading…" : "Upload Photos"}
              <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => uploadPhotos([...e.target.files])} />
            </label>
          </div>
          {form.photos.length > 0 && (
            <div className="grid grid-cols-4 gap-2" data-testid="past-event-photos">
              {form.photos.map((p, i) => (
                <div key={i} className="relative group rounded-lg overflow-hidden border border-[#E2E8E4]">
                  <img src={p} alt={`Upload ${i + 1}`} className="w-full h-16 object-cover" />
                  {i === 0 && <span className="absolute bottom-0 inset-x-0 bg-[#F4A261] text-white text-[8px] text-center uppercase tracking-wide">Cover</span>}
                  <button type="button" onClick={() => setForm((f) => ({ ...f, photos: f.photos.filter((_, x) => x !== i) }))} data-testid={`past-event-photo-remove-${i}`} aria-label="Remove photo"
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mt-5 flex gap-3">
          <button disabled={saving || uploading} data-testid="past-event-save"
            className="inline-flex items-center gap-2 rounded-full bg-[#1E352A] text-[#FAF9F6] px-6 py-2.5 text-xs tracking-[0.12em] uppercase transition-colors duration-300 hover:bg-[#F4A261] hover:text-white disabled:opacity-60">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} {editingId ? "Save Changes" : "Add Event"}
          </button>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm(empty); }} data-testid="past-event-cancel"
              className="inline-flex items-center gap-2 rounded-full border border-[#1E352A]/25 px-6 py-2.5 text-xs tracking-[0.12em] uppercase text-[#5C7368]">
              <X size={13} /> Cancel
            </button>
          )}
        </div>
      </form>

      <div className="col-span-12 lg:col-span-7 space-y-4" data-testid="past-events-list">
        {events.length === 0 && <p className="text-sm text-[#5C7368]">No past events yet. Add your first one.</p>}
        {events.map((ev) => (
          <div key={ev.id} className="rounded-2xl border border-[#E2E8E4] bg-white/60 p-5 flex items-start gap-4" data-testid={`past-event-row-${ev.id.slice(0, 8)}`}>
            {ev.cover
              ? <img src={ev.cover} alt={ev.title} className="w-20 h-20 rounded-xl object-cover border border-[#E2E8E4] shrink-0" />
              : <span className="w-20 h-20 rounded-xl bg-[#C4D7D1] flex items-center justify-center shrink-0"><ImageIcon size={20} className="text-[#1E352A]" /></span>}
            <div className="flex-1">
              <h4 className="font-display text-xl text-[#1A2F24]">{ev.title}</h4>
              <p className="text-xs text-[#F4A261] uppercase tracking-[0.15em] mt-1">{ev.date}</p>
              <p className="text-sm text-[#5C7368] mt-2 line-clamp-2">{ev.description}</p>
              <p className="text-xs text-[#5C7368] mt-1">{ev.photos?.length || 0} photo(s)</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => { setEditingId(ev.id); setForm({ title: ev.title, date: ev.date, sort_date: ev.sort_date, description: ev.description, photos: ev.photos || [] }); }}
                data-testid={`past-event-edit-${ev.id.slice(0, 8)}`} aria-label="Edit past event"
                className="w-9 h-9 rounded-full border border-[#E2E8E4] flex items-center justify-center text-[#5C7368] hover:border-[#F4A261] hover:text-[#F4A261] transition-colors duration-300">
                <Pencil size={14} />
              </button>
              <button onClick={() => remove(ev.id)} data-testid={`past-event-delete-${ev.id.slice(0, 8)}`} aria-label="Delete past event"
                className="w-9 h-9 rounded-full border border-[#E2E8E4] flex items-center justify-center text-[#5C7368] hover:border-red-400 hover:text-red-600 transition-colors duration-300">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PastEventsTab;
